import { useEffect, useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { Input, Select } from "../components/ui/Fields";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { DailySignInReward, MallItem } from "../api/types";

/**
 * Round 45 client item 8 — Daily Sign-in / Free Coins (migration 0106).
 * Configures the consecutive-day reward cycle Android's sign-in screen
 * reads from the public daily_signin_rewards_with_item view; the actual
 * grant (coin_ledger insert or user_mall_items insert) only ever happens
 * inside claim_daily_signin(), called by the Worker's own
 * /daily-signin/claim route — this page only edits the catalog, exactly
 * the same split as Mall Items vs. the real buy_mall_item() RPC.
 *
 * Each day is exactly one of a coin reward or a Mall item reward — the
 * form below enforces that the same way routes/admin/daily-signin.js's
 * validateAndBuildRow does server-side, and the DB's own check
 * constraint (daily_signin_rewards_reward_shape) backs it up either way.
 * Cycle length is simply "however many active day rows exist" — there's
 * no separate setting for it; deactivating the highest day shortens the
 * cycle, adding a new highest day extends it.
 */
export function DailySignInPage() {
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } =
    usePagedList<DailySignInReward>("/admin/daily-signin");
  const [mallItems, setMallItems] = useState<MallItem[]>([]);
  const [editing, setEditing] = useState<Partial<DailySignInReward> | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    apiRequest<MallItem[]>("/admin/mall-items").then(setMallItems).catch(() => {});
  }, []);

  function mallItemName(id: number | null) {
    if (!id) return "—";
    return mallItems.find((i) => i.id === id)?.name ?? `#${id}`;
  }

  async function save() {
    if (!editing?.day_number || editing.day_number < 1) {
      showToast("Day number must be 1 or more", "error");
      return;
    }
    if (!editing.reward_type) {
      showToast("Pick a reward type", "error");
      return;
    }
    if (editing.reward_type === "coins" && (!editing.coins_amount || Number(editing.coins_amount) <= 0)) {
      showToast("Coin amount must be more than zero", "error");
      return;
    }
    if (editing.reward_type === "mall_item" && !editing.mall_item_id) {
      showToast("Pick a Mall item to grant", "error");
      return;
    }
    setIsSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        dayNumber: Number(editing.day_number),
        rewardType: editing.reward_type,
        isActive: editing.is_active ?? true,
      };
      if (editing.reward_type === "coins") {
        body.coinsAmount = Number(editing.coins_amount);
      } else {
        body.mallItemId = Number(editing.mall_item_id);
        body.itemDurationDays = editing.item_duration_days ? Number(editing.item_duration_days) : null;
      }
      if (editing.id) {
        await apiRequest(`/admin/daily-signin/${editing.id}`, { method: "PATCH", body });
      } else {
        await apiRequest("/admin/daily-signin", { method: "POST", body });
      }
      showToast("Saved", "success");
      reload();
      setEditing(null);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not save this day's reward", "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function toggleActive(row: DailySignInReward) {
    try {
      await apiRequest(`/admin/daily-signin/${row.id}`, { method: "PATCH", body: { isActive: !row.is_active } });
      showToast(row.is_active ? "Deactivated" : "Activated", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not update this day's reward", "error");
    }
  }

  async function remove(row: DailySignInReward) {
    if (!window.confirm(`Delete the day ${row.day_number} reward? This cannot be undone.`)) return;
    try {
      await apiRequest(`/admin/daily-signin/${row.id}`, { method: "DELETE" });
      showToast("Deleted", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not delete this day's reward", "error");
    }
  }

  const columns: Column<DailySignInReward>[] = [
    { header: "Day", render: (row) => <span className="font-medium text-ink">Day {row.day_number}</span> },
    {
      header: "Reward",
      render: (row) =>
        row.reward_type === "coins" ? (
          <span>{row.coins_amount?.toLocaleString()} coins</span>
        ) : (
          <span>{mallItemName(row.mall_item_id)} <span className="text-xs text-ink-faint">(Mall item)</span></span>
        ),
    },
    {
      header: "Duration",
      render: (row) =>
        row.reward_type === "mall_item" ? (row.item_duration_days ? `${row.item_duration_days} days` : "Item default") : "—",
    },
    { header: "Status", render: (row) => (row.is_active ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Inactive</Badge>) },
    {
      header: "",
      className: "text-right",
      render: (row) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={() => setEditing(row)}>Edit</Button>
          <Button size="sm" variant={row.is_active ? "danger" : "secondary"} onClick={() => toggleActive(row)}>
            {row.is_active ? "Deactivate" : "Activate"}
          </Button>
          <Button size="sm" variant="danger" onClick={() => remove(row)}>Delete</Button>
        </div>
      ),
    },
  ];

  return (
    <AppShell title="Daily Sign-in">
      <p className="mb-4 max-w-2xl text-xs text-ink-faint">
        This configures the consecutive-day reward cycle Android's Daily Sign-in screen reads from. The cycle
        length is simply however many days are Active here — there's no separate setting for it. Each day grants
        either Gold coins or a Mall item (never both); the actual grant always happens server-side, exactly once
        per real calendar day, and is never trusted from the app. Missing a day resets a user back to Day 1 —
        that logic lives in claim_daily_signin() and isn't configurable from here.
      </p>
      <div className="mb-4 flex justify-end">
        <Button onClick={() => setEditing({ day_number: (rows[rows.length - 1]?.day_number ?? 0) + 1, reward_type: "coins", is_active: true })}>
          + Add Day
        </Button>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No sign-in days configured yet"
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />

      <Modal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Edit Day" : "Add Day"}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} isLoading={isSubmitting}>Save</Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input
            label="Day number"
            type="number"
            value={editing?.day_number ?? 1}
            onChange={(e) => setEditing((p) => ({ ...p, day_number: Number(e.target.value) }))}
          />
          <Select
            label="Reward type"
            value={editing?.reward_type ?? "coins"}
            onChange={(e) =>
              setEditing((p) => ({ ...p, reward_type: e.target.value as DailySignInReward["reward_type"] }))
            }
          >
            <option value="coins">Gold coins</option>
            <option value="mall_item">Mall item</option>
          </Select>

          {editing?.reward_type === "coins" ? (
            <Input
              label="Coin amount"
              type="number"
              value={editing?.coins_amount ?? 0}
              onChange={(e) => setEditing((p) => ({ ...p, coins_amount: Number(e.target.value) }))}
            />
          ) : (
            <>
              <Select
                label="Mall item"
                value={editing?.mall_item_id ?? ""}
                onChange={(e) => setEditing((p) => ({ ...p, mall_item_id: e.target.value ? Number(e.target.value) : null }))}
              >
                <option value="">Select an item…</option>
                {mallItems.map((i) => (
                  <option key={i.id} value={i.id}>{i.name}</option>
                ))}
              </Select>
              <Input
                label="Duration override in days (optional — blank uses the item's own duration)"
                type="number"
                value={editing?.item_duration_days ?? ""}
                onChange={(e) =>
                  setEditing((p) => ({ ...p, item_duration_days: e.target.value ? Number(e.target.value) : null }))
                }
              />
            </>
          )}

          <Select
            label="Status"
            value={editing?.is_active === false ? "off" : "on"}
            onChange={(e) => setEditing((p) => ({ ...p, is_active: e.target.value === "on" }))}
          >
            <option value="on">Active — part of the live cycle</option>
            <option value="off">Inactive — hidden from the cycle</option>
          </Select>
        </div>
      </Modal>
    </AppShell>
  );
}
